import { html, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import style from './maturity-and-duration.scss?inline';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import {
  Constants,
  VIEWS,
  DISPLAY_TAB,
} from './maturity-and-duration.constants.js';
import type { MaturityAndDurationContentObject } from './maturity-and-duration.model.js';
import { MaturityAndDurationItems } from './maturity-and-duration.model.js';
import { isNullish } from '../../common/utils/nullish';

/**
 * @tag pop-maturity-and-duration-component
 * @attribute { MaturityAndDurationContentObject } [data-component-object] MaturityAndDuration content object
 */
@customElement(Constants.TAG_NAME)
export class PopMaturityAndDurationComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * MaturityAndDuration content object
   */
  componentObject?: MaturityAndDurationContentObject;

  /**
   * @internal
   */
  @state()
  selectedTab: string = Constants.DEFAULT_TAB;

  /**
   * @internal
   */
  @state()
  view: string = Constants.DEFAULT_VIEW;

  private renderTopSection() {
    return html`
      <h3
        class="maturity-and-duration-heading b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}
      </h3>
    `;
  }

  private renderControlSection() {
    return html`
      <div
        class="maturity-and-duration-controls b-margin-block-start--micro--jumbo"
      >
        ${this.renderAsOfDate()} ${this.renderViewDropdown()}
      </div>
    `;
  }

  private renderAsOfDate() {
    return html`
      <beacon-control>
        <label slot="label" for="${Constants.DATA_AS_OF_DROPDOWN_ID}">
          ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
        </label>
        <select
          id="${Constants.DATA_AS_OF_DROPDOWN_ID}"
          @change="To be implemented in a future MR"
        >
          <option value="2025-01-01" selected>2025-01-01</option>
          <option value="2025-02-01">2025-02-01</option>
          <option value="2025-03-01">2025-03-01</option>
          <option value="2025-04-01">2025-04-01</option>
        </select>
      </beacon-control>
    `;
  }

  private renderViewDropdown() {
    return html` <beacon-control>
      <label slot="label" for="${Constants.VIEW_DROPDOWN_ID}"
        >${Constants.VIEW_DROPDOWN_LABEL}</label
      >
      <select
        id="${Constants.VIEW_DROPDOWN_ID}"
        @change="${this.viewChangeHandler}"
      >
        ${Object.values(VIEWS).map((view) => {
          return html`
            <option value="${view}" ?selected=${view === this.view}>
              ${view}
            </option>
          `;
        })}
      </select>
    </beacon-control>`;
  }

  private viewChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value;
    if (value) {
      this.view = value;
    } else {
      console.warn(`Invalid view value: ${value}`);
    }
  }

  private renderMaturityTable() {
    const itemList = this.componentObject?.maturityItems || [];
    const sortedItemList = this.sortItemListByRank(itemList);
    return html`
      <div
        class="maturity-and-duration-table b-margin-block-start--micro--jumbo"
      >
        <pop-maturity-and-duration-table-component
          data-component-object="${JSON.stringify({
            label: Constants.MATURITY_TAB_LABEL,
            showBenchmarkData: this.componentObject?.hasBenchmarkData,
            itemList: sortedItemList,
          })}"
        ></pop-maturity-and-duration-table-component>
      </div>
    `;
  }

  private renderDurationTable() {
    const itemList = this.componentObject?.durationItems || [];
    const sortedItemList = this.sortItemListByRank(itemList);
    return html`
      <div
        class="maturity-and-duration-table b-margin-block-start--micro--jumbo"
      >
        <pop-maturity-and-duration-table-component
          data-component-object="${JSON.stringify({
            label: Constants.DURATION_TAB_LABEL,
            showBenchmarkData: this.componentObject?.hasBenchmarkData,
            itemList: sortedItemList,
          })}"
        ></pop-maturity-and-duration-table-component>
      </div>
    `;
  }

  private renderMaturityChart() {
    const itemList = this.componentObject?.maturityItems || [];
    const sortedItemList = this.sortItemListByRank(itemList);

    return html`
      <div
        class="maturity-and-duration-chart b-margin-block-start--micro--jumbo"
      >
        <pop-maturity-and-duration-chart-component
          data-component-object="${JSON.stringify({
            label: Constants.MATURITY_TAB_LABEL,
            showBenchmarkData: this.componentObject?.hasBenchmarkData,
            itemList: sortedItemList,
          })}"
        ></pop-maturity-and-duration-chart-component>
      </div>
    `;
  }

  private renderDurationChart() {
    const itemList = this.componentObject?.durationItems || [];
    const sortedItemList = this.sortItemListByRank(itemList);

    return html`
      <div
        class="maturity-and-duration-chart b-margin-block-start--micro--jumbo"
      >
        <pop-maturity-and-duration-chart-component
          data-component-object="${JSON.stringify({
            label: Constants.DURATION_TAB_LABEL,
            showBenchmarkData: this.componentObject?.hasBenchmarkData,
            itemList: sortedItemList,
          })}"
        ></pop-maturity-and-duration-chart-component>
      </div>
    `;
  }

  private renderMaturityContent() {
    return html`
      ${this.view === VIEWS.TABLE
        ? this.renderMaturityTable()
        : this.renderMaturityChart()}
      ${this.renderBenchmarkName()}
    `;
  }

  private renderDurationContent() {
    return html`
      ${this.view === VIEWS.TABLE
        ? this.renderDurationTable()
        : this.renderDurationChart()}
      ${this.renderBenchmarkName()}
    `;
  }

  /**
   * Sort the itemList based on rank
   * @param itemList - The list of items to be sorted
   * @returns - The sorted list of items
   * */
  private sortItemListByRank(
    itemList: MaturityAndDurationItems[],
  ): MaturityAndDurationItems[] {
    return itemList
      .filter(
        (item): item is { rank: number } =>
          !isNullish(item.rank) && typeof item.rank === 'number',
      )
      .sort((a, b) => a.rank - b.rank);
  }

  renderBenchmarkName() {
    return html`<div
      class="maturity-and-duration-benchmark b-margin-block-start--micro--jumbo"
    >
      <p>Benchmark content goes here</p>
    </div>`;
  }

  private renderDisclaimerSection() {
    return html`<div
      class="maturity-and-duration-disclaimer b-margin-block-start--micro--jumbo"
    >
      <p>Disclaimer content goes here</p>
    </div>`;
  }

  private renderTabSection() {
    return html`
      <beacon-tabs variant="secondary" tab-type="standard">
        <beacon-tab
          slot="tab-children"
          label="${Constants.MATURITY_TAB_LABEL}"
          @beacon-event-click="${() => {
            this.selectedTab = DISPLAY_TAB.MATURITY;
          }}"
        ></beacon-tab>
        <beacon-tab
          slot="tab-children"
          label="${Constants.DURATION_TAB_LABEL}"
          @beacon-event-click="${() => {
            this.selectedTab = DISPLAY_TAB.DURATION;
          }}"
        ></beacon-tab>
        <beacon-tab-panel slot="tab-panel">
          ${this.renderMaturityTab()}
        </beacon-tab-panel>
        <beacon-tab-panel slot="tab-panel">
          ${this.renderDurationTab()}
        </beacon-tab-panel>
      </beacon-tabs>
    `;
  }

  private renderMaturityTab() {
    if (this.selectedTab !== DISPLAY_TAB.MATURITY) {
      return nothing;
    }

    const maturityItemList = this.componentObject?.maturityItems || [];
    if (!maturityItemList || maturityItemList.length === 0) {
      return html`<pop-no-data-available-component
        .message="${Constants.MATURITY_UNAVAILABLE}"
      ></pop-no-data-available-component>`;
    }

    return html`
      ${this.renderControlSection()} ${this.renderMaturityContent()}
      ${this.renderDisclaimerSection()}
    `;
  }

  private renderDurationTab() {
    if (this.selectedTab !== DISPLAY_TAB.DURATION) {
      return nothing;
    }

    const durationItemList = this.componentObject?.durationItems || [];
    if (!durationItemList || durationItemList.length === 0) {
      return html`<pop-no-data-available-component
        .message="${Constants.DURATION_UNAVAILABLE}"
      ></pop-no-data-available-component>`;
    }

    return html`
      ${this.renderControlSection()} ${this.renderDurationContent()}
      ${this.renderDisclaimerSection()}
    `;
  }

  render() {
    return this.renderComponent(html`
      <section class="maturity-and-duration-content b-container">
        ${this.renderTopSection()} ${this.renderTabSection()}
      </section>
    `);
  }
}
